import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, Legend, Tooltip, defaults } from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import { capitalize } from '@crazyegginc/hatch';

import { useTimeZonedFunctions, useChartTooltip } from '/src/hooks';
import { abbreviateNumber, formatCurrency } from '/src/utils';
import { goalSelectedDatesAtom } from '/src/features/goals/store';
import { DISTRIBUTION_DATASET_TYPES, COLOR_MATRIX } from '/src/features/goals/constants';
import { goalDistributionQuery } from '/src/features/goals/queries';
import { ROOT_ELEMENT_ID } from '/src/features/_global/constants';

import { SkeletonChart } from '/src/components/SkeletonChart';
import { ChartTooltipArrow } from '/src/components/ChartTooltipArrow';
import { BarShineOverlay } from '/src/chartjs-plugins/BarShineOverlay';

const ZeroBarLine = {
  id: 'ZeroBarLine',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta?.data?.length) return;
      meta.data.forEach((bar, index) => {
        const raw = dataset.data?.[index];
        if (raw !== 0) return;
        const { x, base, width } = bar.getProps(['x', 'base', 'width'], true);
        if (!width) return;
        const fill = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] : dataset.backgroundColor;
        const height = 2;
        ctx.save();
        ctx.fillStyle = fill || '#a5acbc';
        ctx.fillRect(x - width / 2, base - height, width, height);
        ctx.restore();
      });
    });
  },
};

Tooltip.positioners.topOfBars = function (elements, eventPosition) {
  if (!elements || elements.length === 0) return eventPosition;

  // Keep only bar elements that are actually drawn
  const bars = elements
    .map((el) => el?.element ?? el) // Chart.js may wrap the element
    .filter((el) => el && typeof el.getProps === 'function');

  if (bars.length === 0) return eventPosition;

  // Center X between bars (average of their x positions)
  const xs = bars.map((bar) => bar.getProps(['x'], true).x);
  const x = xs.reduce((a, b) => a + b, 0) / xs.length;

  const topYs = bars.map((bar) => {
    const { y, base } = bar.getProps(['y', 'base'], true);
    return Math.min(y, base);
  });

  const y = Math.min(...topYs);

  return { x, y, xAlign: 'center', yAlign: 'top' };
};

defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';
ChartJS.register(Legend, Tooltip);

const tooltipOptions = { forceDirection: 'top' };

export function DistributionChart({ goal, triggerSelection, dataset, isSeparate }) {
  const { id } = useParams();
  const [selection] = useAtom(goalSelectedDatesAtom);
  const { tooltip, external } = useChartTooltip(tooltipOptions);
  const { getConvertedDateRange, formatChartLabel, formatChartTooltip } = useTimeZonedFunctions();
  const dateRangeResolverOptions = useMemo(
    () => ({
      featureStartedAt: goal?.createdAt ?? undefined,
    }),
    [goal?.createdAt],
  );
  const convertedDateRange = useMemo(
    () =>
      getConvertedDateRange(
        {
          start_date: selection.startDate,
          end_date: selection.endDate,
          special: selection.special,
        },
        dateRangeResolverOptions,
      ),
    [dateRangeResolverOptions, getConvertedDateRange, selection],
  );

  const { data, isFetching } = useQuery({
    ...goalDistributionQuery({
      id: Number(id),
      startDate: convertedDateRange.startDate,
      endDate: convertedDateRange.endDate,
      timeUnit: convertedDateRange.timeUnit,
    }),
  });

  const distData = data?.goalDistribution;
  const currency = distData?.[0]?.totalValue?.displayCurrency ?? 'USD';

  const labels = useMemo(() => {
    if (!distData?.length) return null;
    return distData.map((x) => formatChartLabel(x.startAt, convertedDateRange.timeUnit));
  }, [distData, convertedDateRange.timeUnit, formatChartLabel]);

  const distributionData = useMemo(() => {
    const triggersLength = (triggerSelection ?? []).length;
    return {
      labels,
      datasets: triggerSelection.map((t, i) => {
        const stableIndex = goal?.triggers.findIndex((x) => x.id === t.id);
        return {
          label: `${i + 1}.`,
          data: distData?.map((unitResult) => {
            const trigger = unitResult.triggers.find((x) => x.id === t.id);
            switch (dataset) {
              case DISTRIBUTION_DATASET_TYPES.CONVERSIONS:
                return trigger?.conversionsCount ?? 0;
              case DISTRIBUTION_DATASET_TYPES.VISITORS:
                return trigger?.uniqueConversionsCount ?? 0;
              case DISTRIBUTION_DATASET_TYPES.VALUE:
                return (trigger?.totalValue.displayWorth ?? 0) / 10000;
            }
          }),
          categoryPercentage: 0.75,
          maxBarThickness: 120,
          fill: true,
          backgroundColor: COLOR_MATRIX[stableIndex]?.hex ?? '#a5acbc',
          borderRadius: {
            topLeft: isSeparate || i === triggersLength - 1 ? 5 : 0,
            topRight: isSeparate || i === triggersLength - 1 ? 5 : 0,
            bottomLeft: 0,
            bottomRight: 0,
          },
          borderSkipped: 'bottom',
        };
      }),
    };
  }, [labels, dataset, distData, isSeparate, triggerSelection, goal?.triggers]);

  const chartMinWidth = useMemo(() => {
    const labelCount = labels?.length ?? 0;
    if (!labelCount) return 500;
    const triggersCount = isSeparate ? triggerSelection?.length || 1 : 1;
    const barWidth = 12;
    const barGap = 6;
    const groupPadding = 12;
    const groupWidth = Math.max(36, triggersCount * barWidth + Math.max(0, triggersCount - 1) * barGap + groupPadding);
    const computed = labelCount * groupWidth + 40;
    return Math.max(500, Math.ceil(computed));
  }, [labels, triggerSelection, isSeparate]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: {
            color: 'rgb(104, 118, 141)',
            padding: 0,
            autoSkip: false,
          },
          grid: { display: false },
          border: { display: false },
          stacked: !isSeparate,
        },
        y: {
          stacked: !isSeparate,
          beginAtZero: true,
          grace: '5%',
          ticks: {
            callback: (val) => abbreviateNumber(val),
            precision: 0,
            padding: 10,
            maxTicksLimit: 7,
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
        },
      },
      interaction: {
        mode: 'index',
        axis: 'x',
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external,
          position: 'topOfBars',
        },
        legend: {
          display: false,
        },
        BarShineOverlay: {},
      },
    }),
    [isSeparate, external],
  );

  if (!goal) return null;

  return (
    <div className="w-full flex flex-row">
      <div className="flex h-[312px] w-5 items-center justify-center">
        <span className="text-caption text-2xs -rotate-90 transform whitespace-nowrap">
          {isFetching ? '' : getYAxisLabel(dataset, currency)}
        </span>
      </div>
      <div
        data-testid="goal-distribution-scroll"
        className={classNames(
          'relative flex h-[312px] flex-1 overflow-x-auto overflow-y-hidden',
          'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
        )}
      >
        <div className="relative h-[300px] min-w-[500px] w-full" style={{ minWidth: chartMinWidth }}>
          {isFetching ? (
            <SkeletonChart />
          ) : (
            <>
              <Bar data={distributionData} options={options} plugins={[BarShineOverlay, ZeroBarLine]} />
              {renderChartTooltip({
                tooltip,
                label: distData?.[tooltip?.dataIndex]
                  ? formatChartTooltip(
                      distData[tooltip.dataIndex].startAt,
                      distData[tooltip.dataIndex].endAt,
                      convertedDateRange.timeUnit,
                    )
                  : '',
                valueFormatter: getValueFormatter(dataset, currency),
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function renderChartTooltip({ tooltip, label, valueFormatter }) {
  if (!tooltip || !tooltip.visible) return null;
  if (typeof document === 'undefined') return null;
  const root = document.querySelector(`#${ROOT_ELEMENT_ID}`);
  const portalOffset = getPortalOffset(tooltip);
  const content = (
    <ChartTooltip tooltip={tooltip} label={label} valueFormatter={valueFormatter} positionOffset={portalOffset} />
  );

  return root ? createPortal(content, root) : content;
}

function getPortalOffset(tooltip) {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const left = tooltip?.canvasLeft ?? 0;
  const top = tooltip?.canvasTop ?? 0;
  return {
    x: left + window.scrollX,
    y: top + window.scrollY,
  };
}

function ChartTooltip({ tooltip, label, valueFormatter, positionOffset = { x: 0, y: 0 } }) {
  if (!tooltip || !tooltip.visible) return null;
  const { anchorX, anchorY, attachSide, items = [], transform, offsetXMultiplier, offsetYMultiplier, total } = tooltip;

  const GAP = 10; // distance from tooltip to chart point

  let offsetX = offsetXMultiplier * GAP;
  let offsetY = offsetYMultiplier * GAP;

  return (
    <div
      role="tooltip"
      className="absolute pointer-events-none w-[120px] bg-woodsmoke-500/90 rounded text-white p-2.5 text-sm"
      style={{
        left: positionOffset.x + anchorX + offsetX,
        top: positionOffset.y + anchorY + offsetY,
        transform,
      }}
    >
      {label && (
        <div className="font-semibold mb-1.25" data-testid="goal-distribution-tooltip-label">
          {label}
        </div>
      )}

      <div>
        {items?.map((it, idx) => (
          <div key={`${it.datasetIndex}-${it.dataIndex}-${idx}`} className="flex justify-between items-center">
            <div className="flex items-center min-w-0 space-x-2.5">
              <span
                aria-hidden
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{
                  background: it.color,
                }}
              />
            </div>

            <div className="font-semibold" data-testid="goal-distribution-tooltip-value">
              {valueFormatter(it.raw)}
            </div>
          </div>
        ))}
      </div>
      {items.length > 1 ? (
        <>
          <div className="h-px -mx-2.5 bg-lynch-500 my-1" />
          <div className="font-semibold flex justify-end" data-testid="goal-distribution-tooltip-total">
            {valueFormatter(total)}
          </div>
        </>
      ) : null}

      <ChartTooltipArrow side={attachSide} />
    </div>
  );
}

function getYAxisLabel(dataset, currency) {
  if (dataset === DISTRIBUTION_DATASET_TYPES.VALUE) {
    return `Value (${currency})`;
  }
  return capitalize(dataset);
}

function getValueFormatter(dataset, currency) {
  if (dataset === DISTRIBUTION_DATASET_TYPES.VALUE) {
    return (value) => (value == null ? '' : formatCurrency(value, currency));
  }
  return (value) => {
    if (value == null) return '';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return '';
    return numeric.toLocaleString();
  };
}
